// Mirrors OptroImportWizardLayout: holds the selection across the wizard's
// select and review steps.
import { useCallback, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import {
  OptroOpsAuditSummary,
  OptroWorkStepSummary,
  WorkStepWorkpaperDecision,
} from "../../../../models/audit/workStep";
import {
  OptroWorkStepImportWizardContext,
  OptroWorkStepImportWizardValue,
} from "./optroWorkStepImportWizardContext";

export const OptroWorkStepImportWizardLayout = () => {
  // Audits chosen on the first step; the work-step step renders one
  // accordion group per selected audit.
  const [selectedAudits, setSelectedAudits] = useState<OptroOpsAuditSummary[]>(
    [],
  );

  const toggleAudit = useCallback((audit: OptroOpsAuditSummary) => {
    setSelectedAudits((prev) =>
      prev.some((a) => a.id === audit.id)
        ? prev.filter((a) => a.id !== audit.id)
        : [...prev, audit],
    );
  }, []);

  const isAuditSelected = useCallback(
    (id: number) => selectedAudits.some((a) => a.id === id),
    [selectedAudits],
  );

  const [selected, setSelected] = useState<OptroWorkStepSummary[]>([]);

  const toggleWorkStep = useCallback((workStep: OptroWorkStepSummary) => {
    setSelected((prev) => {
      const isAlreadySelected = prev.some(
        (w) => w.externalRef === workStep.externalRef,
      );

      if (isAlreadySelected) {
        return prev.filter((w) => w.externalRef !== workStep.externalRef);
      }

      return [...prev, workStep];
    });
  }, []);

  const isSelected = useCallback(
    (externalRef: string) =>
      selected.some((w) => w.externalRef === externalRef),
    [selected],
  );

  // Bulk-add for select-all: merges by externalRef, keeping any existing
  // selections (and their order) intact.
  const selectMany = useCallback((workSteps: OptroWorkStepSummary[]) => {
    setSelected((prev) => {
      const existing = new Set(prev.map((w) => w.externalRef));
      const additions = workSteps.filter((w) => !existing.has(w.externalRef));
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, []);

  // Bulk-remove for a group's header checkbox: unchecking an audit group
  // clears only that group's rows from the selection.
  const deselectMany = useCallback((externalRefs: string[]) => {
    setSelected((prev) => {
      const removals = new Set(externalRefs);
      return prev.filter((w) => !removals.has(w.externalRef));
    });
  }, []);

  const [workpaperDecisions, setWorkpaperDecisions] = useState<
    Record<string, WorkStepWorkpaperDecision>
  >({});

  const setWorkpapers = useCallback(
    (externalRef: string, decision: WorkStepWorkpaperDecision) => {
      setWorkpaperDecisions((prev) => ({ ...prev, [externalRef]: decision }));
    },
    [],
  );

  const clearSelection = useCallback(() => {
    setSelected([]);
    setWorkpaperDecisions({});
  }, []);

  const value = useMemo<OptroWorkStepImportWizardValue>(
    () => ({
      selectedAudits,
      toggleAudit,
      isAuditSelected,
      selected,
      toggleWorkStep,
      isSelected,
      selectMany,
      deselectMany,
      clearSelection,
      workpaperDecisions,
      setWorkpapers,
    }),
    [
      selectedAudits,
      toggleAudit,
      isAuditSelected,
      selected,
      toggleWorkStep,
      isSelected,
      selectMany,
      deselectMany,
      clearSelection,
      workpaperDecisions,
      setWorkpapers,
    ],
  );

  return (
    <OptroWorkStepImportWizardContext.Provider value={value}>
      <Outlet />
    </OptroWorkStepImportWizardContext.Provider>
  );
};
